import { Page, pickSeoProps } from '@components/Page'
import { RolexBreadcrumbs } from '@components/_rolex/Breadcrumbs'
import { RolexImage } from '@components/_rolex/Image'
import RolexPage from '@components/_rolex/Page'
import KeepExploring from '@components/_rolex/sliders/KeepExploring'
import StoreMap from '@components/_rolex/store-map'
import StoreMapInfoPanel from '@components/_rolex/store-map/infoPanel'
import { Container } from '@components/gassan-ui'
import { SchemaOrg } from '@components/schema-org'
import { i18nTranslationNamespaces } from '@lib/i18n-translation-files'
import { SingleItemResponse, getCatchAllRolexStorePaths, getPageByCodename } from '@lib/kontent'
import { getPageByTypeAndUrlSlug } from '@lib/kontent/getPageByTypeAndUrlSlug'
import { RolexKeepExploringSliderItem } from '@lib/kontent/models/RolexKeepExploringSliderItem'
import { RolexRcpoContactPageV7 } from '@lib/kontent/models/RolexRcpoContactPageV7'
import { RolexStorePageV72024 } from '@lib/kontent/models/RolexStorePageV72024'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { FC } from 'react'
import { RolexImage as RolexImageModel } from '@lib/kontent/models/RolexImage'
import { RolexPushBannerGrid } from '@components/_rolex/push-banner-grid'
import { cn } from '@lib/cn'

type Props = SingleItemResponse<RolexStorePageV72024> & {
  sid: string
  contactPageContent: SingleItemResponse<RolexRcpoContactPageV7>
}

const StoreDetail: FC<Props> = ({ item, sid, contactPageContent }) => {
  const { t } = useTranslation('rolex')
  const { locale } = useRouter()

  const contactImage = contactPageContent.modular_content[
    contactPageContent.item.elements.image.modular_content[0]
  ].elements as RolexImageModel

  const contactKeepExploringItems =
    contactPageContent.item.elements.keep_exploring_slider_items.value.map(
      (key) => contactPageContent.modular_content[key].elements,
    ) as RolexKeepExploringSliderItem[]

  return (
    <Page {...pickSeoProps(item.elements)} slug={`rolex/contact/${sid}`} noindex={locale === 'de'}>
      <SchemaOrg variant="rolex-store" data={item} />
      <RolexPage pageType="store contact page">
        <RolexBreadcrumbs
          items={[
            {
              label: t('navigation.contactUs', { ns: 'rolex' }),
              href: `/rolex/contact`,
            },
          ]}
          currentPageTitle={item.elements.name.value}
        />
        <Container className="flex !w-full grid-cols-[1fr_auto] flex-col-reverse md:grid md:items-center">
          <StoreMap lat={item.elements.latitude.value} lng={item.elements.longitude.value} />
          <StoreMapInfoPanel item={item} />
        </Container>
        <div
          className={cn(
            'mb-[30px] flex flex-col gap-[30px] bg-rolexLightBeige pb-[30px]',
            'lg:gap-[60px]',
          )}
        >
          <RolexImage
            desktopImage={contactImage.desktop_image}
            mobileImage={contactImage.mobile_image}
            size="xl"
          />
          {contactPageContent.item.elements.featured_push_banner_item.value.map((key) => (
            <RolexPushBannerGrid
              {...contactPageContent.modular_content[key].elements}
              banners={contactPageContent.modular_content[key].elements.items.value.map(
                (key) => contactPageContent.modular_content[key].elements,
              )}
            />
          ))}
        </div>
        <KeepExploring className="py-[30px] md:py-[60px]" items={contactKeepExploringItems} />
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview, params }) => {
  try {
    const sid = params.sid as string
    const language = ['sg', 'de'].includes(locale) ? 'en' : locale
    const content = await getPageByTypeAndUrlSlug<RolexStorePageV72024>({
      language,
      urlSlug: sid,
      type: 'rolex_store_page_v7_2024',
      preview,
    })

    if (!content) {
      return {
        notFound: true,
      }
    }

    const contactPageContent = await getPageByCodename<RolexRcpoContactPageV7>({
      codename: 'rolex_rcpo_contact_page_v7',
      lang: language,
      preview,
    })
    return {
      props: {
        ...content,
        contactPageContent,
        sid,
        ...(await serverSideTranslations(language, i18nTranslationNamespaces)),
      },
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const getStaticPaths = async () => {
  const paths = await getCatchAllRolexStorePaths()

  return {
    fallback: false,
    paths,
  }
}

export default StoreDetail
